<script setup lang="ts">
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useTagEditor } from '@/composables/useTagEditor'
import SearchBox from '@/components/SearchBox.vue'
import UiGroup from '@/components/UiGroup.vue'
import UiList from '@/components/UiList.vue'
import TagNameInput from '@/components/todos/TagNameInput.vue'
import { searchAliases } from '@/todos/tagSearch'
import { chipStyle } from '@/todos/tags'
import UiRow from '@/components/UiRow.vue'

// The other writings of a tag, turned into the tag on every sync: a chip each with a remove button, a field to type a new
// one with suggestions of the tags in use on todos.
const props = defineProps<{ tagId: number }>()
const { t } = useI18n()
const editor = useTagEditor()
const { aliasDrafts, candidates, todosLoading, todosRequested } = editor
const tag = computed(() => editor.tags.value.find((existing) => existing.id === props.tagId))
// (alphabetical like the tags themselves, whatever order the server sends them in)
const sorted = computed(() =>
  [...(tag.value?.aliases ?? [])].sort((a, b) => a.alias.localeCompare(b.alias)),
)
// A search box from the sixth alias on; the search is local to the page and not kept in the URL.
const SEARCH_FROM = 5
const query = ref('')
const searchable = computed(() => sorted.value.length > SEARCH_FROM)
const aliases = computed(() =>
  searchable.value ? searchAliases(sorted.value, query.value) : sorted.value,
)

async function pick(name: string): Promise<void> {
  if (!tag.value) return
  aliasDrafts[tag.value.id] = name
  await editor.addAlias(tag.value)
}
</script>

<template>
  <div v-if="tag" class="tag-aliases" :data-tag-id="tag.id">
    <UiGroup>
      <p class="crumb">
        <span class="label" :style="chipStyle(tag.color)"># {{ tag.name }}</span>
      </p>
    </UiGroup>
    <UiGroup v-if="searchable">
      <SearchBox
        v-model="query"
        :placeholder="t('list.search')"
        :clear-label="t('list.clearSearch')"
      />
      <template #footer>
        <p v-if="query && !aliases.length">{{ t('settings.tags.noMatches') }}</p>
      </template>
    </UiGroup>
    <UiGroup>
      <template #footer>
        <p v-if="todosLoading">{{ t('common.loading') }}</p>
        <p v-else-if="todosRequested && !candidates.length">
          {{ t('settings.tags.aliasPickNone') }}
        </p>
        <p>{{ t('settings.tags.aliasHint') }}</p>
      </template>
      <UiList class="suggest">
        <UiRow v-for="alias in aliases" :key="alias.id">
          <span class="entry-title">{{ alias.alias }}</span>
          <button
            type="button"
            class="icon"
            :aria-label="t('common.remove')"
            @click="editor.removeAlias(tag, alias.id)"
          >
            ✕
          </button>
        </UiRow>
        <UiRow>
          <TagNameInput
            v-model="aliasDrafts[tag.id]"
            class="alias-input"
            :names="candidates"
            :placeholder="t('settings.tags.aliasPlaceholder')"
            :aria-label="t('settings.tags.aliases')"
            @focus="editor.ensureTodosLoaded"
            @pick="pick"
            @submit="editor.addAlias(tag)"
            @blur="editor.addAlias(tag)"
          />
        </UiRow>
      </UiList>
    </UiGroup>
  </div>
  <p v-else-if="editor.loaded.value" class="ui-hint">{{ t('settings.tags.gone') }}</p>
  <p v-else>{{ t('common.loading') }}</p>
</template>

<style scoped>
.crumb {
  margin: 0;
}

.label {
  padding: 0.1rem 0.5rem;
  border-radius: 999px;
}

.entry-title {
  flex: 1;
  min-width: 0;
  overflow-wrap: anywhere;
}

.ui-list.suggest {
  overflow: visible;
}

.alias-input {
  flex: 1;
  min-width: 0;
}

.icon {
  width: auto;
  padding: 0.1rem 0.4rem;
  background: none;
  color: inherit;
  border: none;
  cursor: pointer;
}
</style>
